import { DateTime } from 'luxon';
import { OrderContext } from 'graphql/libs/shopping-cart-front/src/graphql';
import {
  OrderDetailsItem,
  ParsedDetails,
  SessionStatsDetails,
  SessionStatsPeriod,
  SessionStatsTotals,
  TopPublicSessionOrganizationDetailsItem,
  TopPublicSessionOrganizationGroupDetailsItem,
  VisitDetails,
  VisitsDetails,
} from './session-stats.model';

const EMPTY_VISITS: ReadonlyArray<VisitDetails> = [];
const EMPTY_ORDER_DETAILS: ReadonlyArray<OrderDetailsItem> = [];
const EMPTY_TOP_PUBLIC_SESSION_ORGANIZATIONS: ReadonlyArray<TopPublicSessionOrganizationDetailsItem> =
  [];
const EMPTY_TOP_PUBLIC_SESSION_ORGANIZATION_GROUPS: ReadonlyArray<TopPublicSessionOrganizationGroupDetailsItem> =
  [];

export function buildSessionStatsParsedDetails(
  details: SessionStatsDetails | null | undefined,
): ParsedDetails | null {
  if (!details) {
    return null;
  }

  const res: ParsedDetails = {};

  if (details.orders) {
    let ordersCount = 0;
    const total: Record<string, number> = {};
    const orderDetails: Array<OrderDetailsItem> = [];
    for (const inContext of Object.values(details.orders)) {
      for (const inContextId of Object.values(inContext ?? {})) {
        for (const [currency, stats] of Object.entries(inContextId ?? {})) {
          if (typeof total[currency] === 'undefined') {
            total[currency] = 0;
          }

          total[currency] += stats.total;
          ordersCount += stats.count;
        }
      }
    }
    for (const [context, inContext] of Object.entries(details.orders)) {
      for (const [contextId, inContextId] of Object.entries(inContext ?? {})) {
        let count = 0;
        const totals = Object.entries(inContextId ?? {}).map(
          ([currency, stats]) => {
            count += stats.count;
            return {
              currency,
              total: stats.total,
            };
          },
        );

        if (count <= 0 || !totals.length) {
          continue;
        }

        orderDetails.push({
          id: contextId,
          context: context as OrderContext,
          count,
          totals,
          totalsLabel: formatTotalsLabel(totals),
        });
      }
    }
    const totalSorted = Object.entries(total).sort((a, b) => b[1] - a[1]);
    if (totalSorted.length) {
      res.orders = {
        total: totalSorted[0][1],
        count: ordersCount,
        currency: totalSorted[0][0],
      };
    }
    res.orderDetails = orderDetails
      .sort(
        (a, b) =>
          b.count - a.count ||
          getTotalsValue(b.totals) - getTotalsValue(a.totals),
      )
      .slice(0, 3);
  }

  if (details.topPublicSessionOrganizationsByOrders?.length) {
    res.topPublicSessionOrganizationsByOrders =
      details.topPublicSessionOrganizationsByOrders.map((item) => ({
        ...item,
        totalsLabel: formatTotalsLabel(item.totals),
      }));
  }

  if (details.topPublicSessionOrganizationGroupsByOrders?.length) {
    res.topPublicSessionOrganizationGroupsByOrders =
      details.topPublicSessionOrganizationGroupsByOrders.map((item) => ({
        ...item,
        totalsLabel: formatTotalsLabel(item.totals),
      }));
  }

  if (details.visits) {
    let visitsCount = 0;
    const visitsDetails: VisitsDetails = {};

    for (const [context, inContext] of Object.entries(details.visits)) {
      const visits = Object.entries(inContext ?? {})
        .map(([contextId, count]) => {
          const normalizedCount = Number(count ?? 0);
          visitsCount += normalizedCount;
          return {
            id: contextId,
            count: normalizedCount,
          };
        })
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      if (visits.length) {
        visitsDetails[context as OrderContext] = visits;
      }
    }

    if (visitsCount > 0) {
      res.visits = visitsCount;
      res.visitsDetails = visitsDetails;
    }
  }

  return res;
}

export function getSessionStatsPeriod(
  details: SessionStatsDetails | null | undefined,
): SessionStatsPeriod {
  return details?.period ?? 'DAY';
}

export function getSessionStatsDisplayTitle(
  period: SessionStatsPeriod | null | undefined,
): string {
  if (period === 'WEEK') {
    return 'Twoje statystyki za poprzedni tydzień';
  }

  return 'Twoje wczorajsze statystyki';
}

export function getSessionStatsDisplayRangeLabel(
  details: SessionStatsDetails | null | undefined,
): string | null {
  if (
    typeof details?.rangeStart !== 'number' ||
    typeof details?.rangeEnd !== 'number'
  ) {
    return null;
  }

  const timeZone = details.timeZone || 'UTC';
  const rangeStart = DateTime.fromSeconds(details.rangeStart, {
    zone: timeZone,
  });
  const rangeEnd = DateTime.fromSeconds(details.rangeEnd, {
    zone: timeZone,
  });

  if (!rangeStart.isValid || !rangeEnd.isValid) {
    return null;
  }

  return `${rangeStart.toFormat('dd.LL.yyyy')} - ${rangeEnd.toFormat(
    'dd.LL.yyyy',
  )}`;
}

export function getSessionStatsVisits(
  parsedDetails: ParsedDetails | null | undefined,
  context: OrderContext | null | undefined,
): ReadonlyArray<VisitDetails> {
  if (!context) {
    return EMPTY_VISITS;
  }

  return parsedDetails?.visitsDetails?.[context] ?? EMPTY_VISITS;
}

export function getSessionStatsTopOrderSessions(
  parsedDetails: ParsedDetails | null | undefined,
): ReadonlyArray<OrderDetailsItem> {
  return parsedDetails?.orderDetails ?? EMPTY_ORDER_DETAILS;
}

export function getSessionStatsTopPublicSessionOrganizationsByOrders(
  parsedDetails: ParsedDetails | null | undefined,
): ReadonlyArray<TopPublicSessionOrganizationDetailsItem> {
  return (
    parsedDetails?.topPublicSessionOrganizationsByOrders ??
    EMPTY_TOP_PUBLIC_SESSION_ORGANIZATIONS
  );
}

export function getSessionStatsTopPublicSessionOrganizationGroupsByOrders(
  parsedDetails: ParsedDetails | null | undefined,
): ReadonlyArray<TopPublicSessionOrganizationGroupDetailsItem> {
  return (
    parsedDetails?.topPublicSessionOrganizationGroupsByOrders ??
    EMPTY_TOP_PUBLIC_SESSION_ORGANIZATION_GROUPS
  );
}

function getTotalsValue(totals: SessionStatsTotals): number {
  return totals.reduce((previousValue, { total }) => previousValue + total, 0);
}

function formatTotalsLabel(totals: SessionStatsTotals): string {
  return totals
    .map(({ currency, total }) =>
      new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency,
      }).format(total),
    )
    .join(' + ');
}
